/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Ladda from 'ladda';
import _ from 'lodash';

import InputListView from '@/views/InputListView';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';
import { getFiscalYear } from '@/util/MomentUtils';

import AWPackageContractExtensionCollectionView from '../AWPackageContractExtensionCollectionView';
import UpdateAWPackageInformation from '../../services/UpdateAWPackageInformation';
import GetAWPackageFundingCentersByFundingSourceIDAndRegionGroupID from '../../services/GetAWPackageFundingCentersByFundingSourceIDAndRegionGroupID';
import GetAWPackageCostCentersByFundingCenterID from '../../services/GetAWPackageCostCentersByFundingCenterID';
import CalculateDatesForAWPackage from '../../services/CalculateDatesForAWPackage';

import tmplAWPackageInformationView from './AWPackageInformationView.hbs';

const AWPackageInformationView = View.extend({
  template: tmplAWPackageInformationView,

  ui: {
    informationPart1Form: '.information-pt-1-form',
    fundingSourceRadios: 'input[name="fundingSourceId"]',
    contractExtensionContainer: '.contract-extension-container',
    commandsContainer: '.commands-container',
    crsContainer: '.crs-container',
    trackerNumContainer: '.tracker-num-container',
    btnDateCalc: '.btn-calculate-dates',
    koStatusDateReq: 'input[name="koStatusDateReq"]',
    submittedOn: 'input[name="submittedOn"]',
    regionGroup: '[name="regionGroupId"]',
    objectClass: '[name="objectClassId"]',
    internalOrderNumber: '[name="internalOrderNumberId"]',
    fundingSource: '[name="fundingSourceId"]',
    fundingCenter: '[name="fundingCenterId"]',
    costCenter: '[name="costCenterId"]',
    fundingSourceError: '#fundingSourceError'
  },

  events: {
    'click .add-contract-extension': 'addContractExtension',
    'change @ui.regionGroup': 'onChangeRegionGroupOrFundingSource',
    'change @ui.fundingSource': 'onChangeRegionGroupOrFundingSource',
    'change @ui.fundingCenter': 'onChangeFundingCenter',
    'click @ui.btnDateCalc': 'onClickDateCalc'
  },

  regions: {
    contractExtensionContainer: '@ui.contractExtensionContainer',
    commandsContainer: '@ui.commandsContainer',
    crsContainer: '@ui.crsContainer',
    fundingCentersView: '@ui.fundingCentersView',
    costCentersView: '@ui.costCentersView',
    trackerNumContainer: '@ui.trackerNumContainer'
  },

  onAttach() {
    if (this.model.get('showValidation')) {
      this.validate();
    }
  },

  onRender() {
    const awPackage = this.model.get('awPackage');

    if (!awPackage.fiscalYear) {
      awPackage.fiscalYear = getFiscalYear();
    }

    this.ui.koStatusDateReq.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.koStatusDateReq.datepicker('setDate', awPackage.koStatusDateReq);

    this.ui.submittedOn.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.submittedOn.datepicker('setDate', awPackage.submittedOn);

    this.awPackageContractExtensions = new AWPackageContractExtensionCollectionView({
      collection: new Backbone.Collection(awPackage.awPackageContractExtensions),
      model: new Backbone.Model({
        contractExtensionTypes: this.model.get('contractExtensionTypes'),
        editable: this.model.get('editable')
      })
    });
    this.showChildView('contractExtensionContainer', this.awPackageContractExtensions);

    this.showChildView(
      'commandsContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'All Commands in AWP',
          ratio: 'half',
          type: 'text',
          inputName: 'commandName',
          ariaLabel: 'Command Name',
          isReadOnly: true,
          emptyText: 'No Commands Found'
        }),
        collection: new Backbone.Collection(awPackage.commands)
      })
    );

    this.showChildView(
      'crsContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'All Assigned CRSs',
          ratio: 'half',
          type: 'text',
          inputName: 'name',
          ariaLabel: 'CRS name',
          isReadOnly: true,
          emptyText: 'No CRSs Found'
        }),
        collection: new Backbone.Collection(awPackage.crss)
      })
    );

    const trackerNumbers = _.filter(awPackage.trackerNumbers, (num) => {
      return num.name;
    });
    this.showChildView(
      'trackerNumContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'Tracker #(s)',
          ratio: 'half',
          type: 'text',
          inputName: 'name',
          isReadOnly: true,
          emptyText: 'No Tracker Numbers Found'
        }),
        collection: new Backbone.Collection(trackerNumbers)
      })
    );

    Syphon.deserialize(this.ui.informationPart1Form, awPackage);
    this.awPackageContractExtensions.collection.reset(awPackage.awPackageContractExtensions);

    if (!this.model.get('isFundingSourceEditable')) {
      this.ui.fundingSourceRadios.prop('disabled', true);
    }

    if (!this.model.get('editable')) {
      this.toggleForm(false);
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  addContractExtension(e) {
    e.preventDefault();
    this.getChildView('contractExtensionContainer').options.collection.add({});
  },

  showDateCalcError() {
    showErrorMessage({
      errorCode: 'dateCalcError',
      errorTitle: 'Error Calculating KO Status Date',
      errorMessage: 'An error occurred while calculating the required KO status date.'
    });
  },

  showUnauthorizedDateCalcError(message) {
    showErrorMessage({
      errorCode: 'dateCalcError',
      errorTitle: 'Unauthorized User',
      errorMessage: `${message}, unable to calculate KO Status Date.`
    });
  },

  async onClickDateCalc(e) {
    e.preventDefault();

    const spinner = Ladda.create(this.ui.btnDateCalc[0]);
    spinner.start();

    try {
      const koStatusDateReq = await CalculateDatesForAWPackage(this.model.get('awPackage').id);
      this.ui.koStatusDateReq.val(koStatusDateReq);
    } catch (err) {
      if (err?.message?.string === 'Unauthorized') {
        this.showUnauthorizedDateCalcError(err.message.detail);
      } else {
        this.showDateCalcError();
      }
    } finally {
      spinner.stop();
    }
  },

  async onChangeRegionGroupOrFundingSource() {
    const self = this;
    const regionGroupId = self.ui.regionGroup.val();
    const fundingCenterId = self.ui.fundingCenter.val();
    const fundingSourceId = self.ui.fundingSource.filter(':checked').val();

    const fundingCenters = await GetAWPackageFundingCentersByFundingSourceIDAndRegionGroupID(
      fundingSourceId,
      regionGroupId
    );

    self.ui.fundingCenter.empty();
    self.ui.fundingCenter.append(
      $('<option>', {
        value: '',
        text: '--'
      })
    );

    _.each(fundingCenters, (fc) => {
      self.ui.fundingCenter.append(
        $('<option>', {
          value: fc.id,
          text: fc.name,
          selected: fc.id === fundingCenterId
        })
      );
    });

    //set default values for dropdowns
    self.setObjectClassDefault(regionGroupId);
    self.setInternalOrderNumberDefault(regionGroupId);
    self.setFundingCenterDefault(fundingCenters);

    //add cost center options and set default based on funding center
    self.onChangeFundingCenter();
  },

  async onChangeFundingCenter() {
    const self = this;
    const fundingCenterId = self.ui.fundingCenter.val();
    const costCenterId = self.ui.costCenter.val();

    const costCenters = await GetAWPackageCostCentersByFundingCenterID(fundingCenterId);

    self.ui.costCenter.empty();
    self.ui.costCenter.append(
      $('<option>', {
        value: '',
        text: '--'
      })
    );

    _.each(costCenters, (cc) => {
      self.ui.costCenter.append(
        $('<option>', {
          value: cc.id,
          text: cc.name,
          selected: cc.id === costCenterId
        })
      );
    });

    self.setCostCenterDefault(fundingCenterId, costCenters);
  },

  async setObjectClassDefault(regionGroupId) {
    if (!regionGroupId) {
      this.ui.objectClass.val('');
      return;
    }

    const objectClasses = this.model.get('objectClasses');
    const defaultObjectClass = _.filter(objectClasses, (objectClass) => {
      return objectClass.isDefault === '1';
    });

    if (defaultObjectClass.length > 0) {
      this.ui.objectClass.val(defaultObjectClass[0].id);
    }
  },

  async setInternalOrderNumberDefault(regionGroupId) {
    if (!regionGroupId) {
      this.ui.internalOrderNumber.val('');
      return;
    }

    const internalOrderNumbers = this.model.get('internalOrderNumbers');
    const defaultInternalOrderNumber = _.filter(internalOrderNumbers, (intOrderNumber) => {
      return intOrderNumber.regionGroupId === regionGroupId;
    });

    if (defaultInternalOrderNumber.length > 0) {
      this.ui.internalOrderNumber.val(defaultInternalOrderNumber[0].id);
    }
  },

  async setFundingCenterDefault(fundingCenters) {
    const defaultFundingCenter = _.filter(fundingCenters, (fundingCenter) => {
      return fundingCenter.isDefault === '1';
    });

    if (defaultFundingCenter.length > 0) {
      this.ui.fundingCenter.val(defaultFundingCenter[0].id);
    }
  },

  async setCostCenterDefault(fundingCenterId, costCenters) {
    if (!fundingCenterId) {
      this.ui.costCenter.val('');
      return;
    }

    const defaultCostCenter = _.filter(costCenters, (costCenter) => {
      return costCenter.fundingCenterId === fundingCenterId;
    });

    if (defaultCostCenter.length > 0) {
      this.ui.costCenter.val(defaultCostCenter[0].id);
    }
  },

  async validate() {
    const isEditable = this.model.get('editable');
    try {
      if (!isEditable) {
        this.ui.informationPart1Form
          .find(':input[data-parsley-required="true"][disabled], select[data-parsley-required="true"][disabled]')
          .prop('disabled', false);
      }
      await Promise.all([validateForm(this.ui.informationPart1Form)]);
      this.trigger('no:errors');
      return true;
    } catch (err) {
      this.trigger('has:errors');
      return false;
    } finally {
      if (!isEditable) {
        this.toggleForm(false);
      }
    }
  },

  async onSave() {
    if (!this.model.get('editable')) {
      return;
    }

    const formData = Syphon.serialize(this.ui.informationPart1Form);
    formData.awPackageContractExtensions = this.awPackageContractExtensions.getFormData();
    formData.awPackageId = this.model.get('awPackage').id;

    try {
      await UpdateAWPackageInformation(formData);
      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'AW Package Information Saved.',
        hideAfter: 1
      });
    } catch (err) {
      showErrorMessage({
        errorCode: 'saveError',
        errorTitle: 'Save Error',
        errorMessage: 'Error Saving File.'
      });
      throw new Error(err);
    }
  }
});

export default AWPackageInformationView;
